import React, { useEffect, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { BADGES, formatINR } from '../../../data/games';

const TABS = [
  { id: 'overview', label: 'Overview', icon: 'Info' },
  { id: 'rules', label: 'How to play', icon: 'BookOpen' },
  { id: 'strategy', label: 'Strategy', icon: 'Brain' },
];

const GamePreviewModal = ({ game, isOpen, onClose, onWhatsAppClick }) => {
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => setTab('overview'), [game?.id]);

  if (!isOpen || !game) return null;

  const badge = BADGES[game.badge] ?? BADGES.classic;

  const rules = game.rules ?? [
    'Choose your stake and confirm the table limits with the desk',
    'Every round is dealt from an audited shoe or certified RNG',
    'Winnings settle to your wallet the moment the round closes',
    'Withdraw any time — the cashier runs 24×7',
  ];

  const strategies = game.strategies ?? [
    { title: 'Set a session limit', description: 'Decide your stop-loss and stop-win before the first hand. Our desk can lock limits on your account if you ask.' },
    { title: 'Stake small, stay long', description: 'Keep each bet under 2% of your bankroll so a cold streak never ends the night early.' },
    { title: 'Use the free coaching', description: 'Message the VIP desk for the strategy pack written for this exact table — it is free and takes two minutes to read.' },
  ];

  return (
    <div className="fixed inset-0 z-modal flex items-end justify-center p-0 sm:items-center sm:p-6">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-fade-in" onClick={onClose} aria-hidden="true" />

      <div className="perspective relative w-full max-w-3xl">
        <div className="animate-pop-3d max-h-[92vh] overflow-y-auto overflow-x-hidden rounded-t-[28px] border border-white/12 bg-[#0B0B14] shadow-[0_50px_100px_-30px_rgba(0,0,0,1)] sm:rounded-[28px] no-scrollbar">
          {/* Hero */}
          <div className="relative h-52 sm:h-64">
            <Image src={game.image} alt={game.name} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B14] via-[#0B0B14]/40 to-transparent" />

            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/60 text-white backdrop-blur transition hover:rotate-90 hover:border-royal-gold/60"
            >
              <Icon name="X" size={18} />
            </button>

            <div className="absolute bottom-5 left-6 right-6">
              <span className={`inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r ${badge.className} px-3 py-1 text-[0.6rem] font-bold text-[#150F02]`}>
                <Icon name={badge.icon} size={11} color="#150F02" /> {badge.label}
              </span>
              <h2 className="mt-2 font-heading text-3xl font-bold text-white">{game.name}</h2>
              <p className="text-sm text-royal-gold">{game.tagline}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 border-b border-white/8 px-6 py-5 sm:grid-cols-4">
            {[
              { l: 'Min bet', v: formatINR(game.minBet), c: '#F0C24B' },
              { l: 'Max win', v: formatINR(game.maxWin), c: '#25D366' },
              { l: 'Rating', v: `${game.rating}★`, c: '#FF8ED0' },
              { l: 'Playing now', v: game.playerCount?.toLocaleString('en-IN'), c: '#5B8CFF' },
            ].map((s) => (
              <div key={s.l} className="rounded-xl border border-white/8 bg-white/[0.04] px-3 py-2.5 text-center">
                <p className="text-[0.58rem] uppercase tracking-[0.14em] text-white/40">{s.l}</p>
                <p className="mt-0.5 font-display text-sm font-bold" style={{ color: s.c }}>{s.v}</p>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-white/8 px-6 py-3">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition ${
                  tab === t.id
                    ? 'bg-gradient-royal text-[#150F02]'
                    : 'border border-white/10 bg-white/[0.05] text-white/60 hover:text-white'
                }`}
              >
                <Icon name={t.icon} size={14} /> {t.label}
              </button>
            ))}
          </div>

          {/* Panel */}
          <div className="px-6 py-6">
            {tab === 'overview' && (
              <div className="animate-fade-in space-y-5">
                <p className="text-sm leading-relaxed text-white/65">{game.fullDescription ?? game.description}</p>
                <div>
                  <h4 className="mb-3 font-display text-sm font-semibold text-white">What is on the table</h4>
                  <div className="flex flex-wrap gap-2">
                    {game.features?.map((f) => (
                      <span key={f} className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 text-xs text-white/70">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
                {game.recentWinner && (
                  <div className="flex items-center gap-3 rounded-2xl border border-wa-green/25 bg-wa-green/8 px-4 py-3">
                    <Icon name="Trophy" size={18} color="#25D366" />
                    <p className="text-sm text-white/70">
                      Latest payout: <strong className="text-white">{game.recentWinner.name}</strong> took home{' '}
                      <strong className="text-wa-green">{formatINR(game.recentWinner.amount)}</strong>
                    </p>
                  </div>
                )}
              </div>
            )}

            {tab === 'rules' && (
              <ol className="animate-fade-in space-y-3">
                {rules.map((r, i) => (
                  <li key={r} className="flex gap-3 rounded-2xl border border-white/8 bg-white/[0.04] px-4 py-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-royal font-display text-xs font-bold text-[#150F02]">
                      {i + 1}
                    </span>
                    <span className="text-sm text-white/65">{r}</span>
                  </li>
                ))}
              </ol>
            )}

            {tab === 'strategy' && (
              <div className="animate-fade-in space-y-3">
                {strategies.map((s) => (
                  <div key={s.title} className="rounded-2xl border border-white/8 bg-white/[0.04] p-4">
                    <h4 className="flex items-center gap-2 font-display text-sm font-semibold text-royal-gold">
                      <Icon name="Lightbulb" size={15} /> {s.title}
                    </h4>
                    <p className="mt-1.5 text-sm text-white/60">{s.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer CTA */}
          <div className="sticky bottom-0 flex flex-col gap-3 border-t border-white/8 bg-[#0B0B14]/95 px-6 py-4 backdrop-blur sm:flex-row sm:items-center">
            <p className="flex-1 text-xs text-white/45">
              Seats are confirmed on WhatsApp in under two minutes — limits, bonus and cashier set up for you.
            </p>
            <button onClick={() => onWhatsAppClick(game)} className="btn-3d btn-3d-wa !py-3 text-sm">
              <FaWhatsapp size={17} /> Play {game.name}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GamePreviewModal;
